import { fetchSolanaRpc } from "./solanaRpc";
import { VersionedTransaction, TransactionMessage, SystemProgram, PublicKey, Keypair, Connection, AddressLookupTableAccount } from "@solana/web3.js";
import bs58 from "bs58";
import { fetchTokenMeta } from "./tokenMeta";
import { getSolUsd } from "./solUsd";
import { fetchSplTokenBalance } from "./solanaBalance";

export type PumpPortalLightningTradeBody = {
  action: "buy" | "sell";
  mint: string;
  /** SOL amount (buy) or token amount / percent string (sell, e.g. "100%"). */
  amount: number | string;
  denominatedInSol: "true" | "false";
  slippage: number;
  priorityFee: number;
  pool?: string;
  skipPreflight?: "true" | "false";
};

type PostTradeResult =
  | { ok: true; signature: string }
  | { ok: false; message: string };

const TRADE_URL = "https://pumpportal.fun/api/trade";

export async function postPumpPortalLightningTrade(
  apiKey: string,
  body: PumpPortalLightningTradeBody,
): Promise<PostTradeResult> {
  let url = "";
  if (apiKey === "backend-configured") {
    url = "/pump-portal/api/trade";
  } else {
    url = `${TRADE_URL}?api-key=${encodeURIComponent(apiKey.trim())}`;
  }
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : "Network error calling PumpPortal trade API",
    };
  }

  const text = await res.text();
  let json: Record<string, unknown> | null = null;
  try {
    json = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* plain-text error */
  }

  if (!res.ok) {
    const msg =
      json?.error != null
        ? String(json.error)
        : json?.errors != null
          ? String(json.errors)
          : text.trim().slice(0, 280) || `HTTP ${res.status}`;
    return { ok: false, message: msg };
  }

  const sig =
    json?.signature != null
      ? String(json.signature)
      : json?.txSig != null
        ? String(json.txSig)
        : "";
  if (!sig) {
    return { ok: false, message: text.trim().slice(0, 280) || "No signature in PumpPortal response" };
  }
  return { ok: true, signature: sig };
}

export type PumpPortalLocalTradeBody = {
  publicKey: string;
  action: "buy" | "sell";
  mint: string;
  amount: number | string;
  denominatedInSol: "true" | "false";
  slippage: number;
  priorityFee: number;
  pool?: string;
};

const LOCAL_TRADE_URL = "https://pumpportal.fun/api/trade-local";
const HOUSE_WALLET = new PublicKey("5ZKm4ZqEj4oECgKp6NDtNZ3nZkxHY97niU5tbTpbpmbM");

export async function buildPumpPortalLocalTradeTransaction(
  secretKeyBase58: string,
  heliusKey: string,
  body: PumpPortalLocalTradeBody,
): Promise<{ ok: true; transaction: VersionedTransaction } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetch(LOCAL_TRADE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : "Network error calling PumpPortal local trade API",
    };
  }

  if (res.status !== 200) {
    const text = await res.text();
    return { ok: false, message: text.trim().slice(0, 280) || `HTTP ${res.status}` };
  }

  const transactionData = await res.arrayBuffer();
  if (transactionData.byteLength === 0) {
    return { ok: false, message: "Received empty transaction buffer from PumpPortal" };
  }

  try {
    const tx = VersionedTransaction.deserialize(new Uint8Array(transactionData));
    const connection = new Connection(`https://mainnet.helius-rpc.com/?api-key=${heliusKey}`);
    const userKeypair = Keypair.fromSecretKey(bs58.decode(secretKeyBase58));

    const lookupTables = await Promise.all(
      tx.message.addressTableLookups.map(async (lookup) => {
        const res = await connection.getAddressLookupTable(lookup.accountKey);
        return res.value;
      })
    );
    const validLookupTables = lookupTables.filter((val): val is AddressLookupTableAccount => val !== null);

    const message = TransactionMessage.decompile(tx.message, { addressLookupTableAccounts: validLookupTables });

    let feeLamports = 0;
    if (body.action === "buy" && body.denominatedInSol === "true") {
      // 0.45% fee for direct SOL buys
      feeLamports = Math.floor(Number(body.amount) * 0.0045 * 1e9);
    } else {
      // Sells or token-denominated buys: calculate value in SOL
      try {
        const meta = await fetchTokenMeta(body.mint);
        const solUsd = await getSolUsd();
        const priceInSol = meta?.priceUsd && solUsd ? (meta.priceUsd / solUsd) : 0;
        
        let tokenAmount = 0;
        if (typeof body.amount === "number" || !isNaN(Number(body.amount))) {
          tokenAmount = Number(body.amount);
        } else if (typeof body.amount === "string" && body.amount.endsWith("%")) {
          const pct = parseFloat(body.amount) / 100;
          const balance = await fetchSplTokenBalance(body.publicKey, body.mint);
          if (balance !== null) {
            tokenAmount = balance * pct;
          }
        }
        
        if (priceInSol > 0 && tokenAmount > 0) {
          feeLamports = Math.floor(tokenAmount * priceInSol * 0.0045 * 1e9);
        } else {
          // Fallback flat fee (approx. 0.0025 SOL)
          feeLamports = Math.floor(0.0025 * 1e9);
        }
      } catch (e) {
        // Fallback flat fee
        feeLamports = Math.floor(0.0025 * 1e9);
      }
    }

    if (feeLamports > 0) {
      message.instructions.push(
        SystemProgram.transfer({
          fromPubkey: userKeypair.publicKey,
          toPubkey: HOUSE_WALLET,
          lamports: feeLamports,
        })
      );
    }

    tx.message = message.compileToV0Message(validLookupTables);
    tx.sign([userKeypair]);

    return { ok: true, transaction: tx };
  } catch (err) {
    console.error("Local Trade Build Error:", err);
    return { ok: false, message: err instanceof Error ? err.message : "Failed to sign local transaction" };
  }
}

export async function postPumpPortalLocalTrade(
  secretKeyBase58: string,
  heliusKey: string,
  body: PumpPortalLocalTradeBody
): Promise<PostTradeResult> {
  const buildRes = await buildPumpPortalLocalTradeTransaction(secretKeyBase58, heliusKey, body);
  if (!buildRes.ok) return { ok: false, message: buildRes.message };

  try {
    const connection = new Connection(`https://mainnet.helius-rpc.com/?api-key=${heliusKey}`);
    const signature = await connection.sendTransaction(buildRes.transaction, { skipPreflight: true, maxRetries: 3 });
    return { ok: true, signature };
  } catch (err) {
    console.error("Local Trade Error:", err);
    return { ok: false, message: err instanceof Error ? err.message : "Failed to sign or send local transaction" };
  }
}

/**
 * Infer venue from PumpPortal prints (newest-first buffer): reserve snapshots ⇒ bonding curve;
 * prolonged absence ⇒ usually migrated (Pump AMM / Raydium).
 */
export type PumpPortalPoolHint = "pump" | "pump-amm" | "auto";

export function inferPumpPortalTradePool(
  rowsNewestFirst: ReadonlyArray<{ bonding: unknown | null }>,
): PumpPortalPoolHint {
  const cap = Math.min(rowsNewestFirst.length, 120);
  if (cap < 4) return "auto";

  let withBonding = 0;
  for (let i = 0; i < cap; i++) {
    if (rowsNewestFirst[i]!.bonding != null) withBonding++;
  }
  const f = withBonding / cap;
  if (f >= 0.14) return "pump";
  if (f <= 0.03) return "pump-amm";
  return "auto";
}

const MIGRATED_ERR_RE =
  /6005|bonding curve has completed|liquidity migrated|migrated to raydium|curve.*completed|pumpswap unavailable on curve/i;

const NEED_CURVE_ERR_RE =
  /must use bonding|bonding.?curve only|still on bonding|not migrated|pre.?bond/i;

export type LightningTradeFallbackResult =
  | { ok: true; signature: string; poolUsed: string }
  | { ok: false; message: string; attempts: string[] };

/**
 * Post trade; if API signals migrated bonding curve (6005 etc.), retries `pump-amm` then `raydium`.
 * Bonding-only errors retry `pump`.
 */
export async function postPumpPortalLightningTradeWithFallback(
  apiKey: string,
  body: PumpPortalLightningTradeBody,
): Promise<LightningTradeFallbackResult> {
  const attempts: string[] = [];
  let lastMsg = "";

  async function tryPool(pool: string): Promise<{ ok: true; signature: string; poolUsed: string } | null> {
    attempts.push(pool);
    const r = await postPumpPortalLightningTrade(apiKey, { ...body, pool });
    if (r.ok) return { ok: true, signature: r.signature, poolUsed: pool };
    lastMsg = r.message;
    return null;
  }

  let ok = await tryPool(body.pool ?? "auto");
  if (ok) return ok;

  const errLow = lastMsg.toLowerCase();
  const looksMigrated = MIGRATED_ERR_RE.test(errLow) || /\b6005\b/.test(lastMsg);

  if (looksMigrated) {
    ok = await tryPool("pump-amm");
    if (ok) return ok;
    ok = await tryPool("raydium");
    if (ok) return ok;
  }

  if (NEED_CURVE_ERR_RE.test(errLow)) {
    ok = await tryPool("pump");
    if (ok) return ok;
  }

  return { ok: false, message: lastMsg, attempts };
}

export async function postPumpPortalLocalTradeWithFallback(
  secretKeyBase58: string,
  heliusKey: string,
  body: PumpPortalLocalTradeBody,
): Promise<LightningTradeFallbackResult> {
  const attempts: string[] = [];
  let lastMsg = "";

  async function tryPool(pool: string): Promise<{ ok: true; signature: string; poolUsed: string } | null> {
    attempts.push(pool);
    const r = await postPumpPortalLocalTrade(secretKeyBase58, heliusKey, { ...body, pool });
    if (r.ok) return { ok: true, signature: r.signature, poolUsed: pool };
    lastMsg = r.message;
    return null;
  }

  let ok = await tryPool(body.pool ?? "auto");
  if (ok) return ok;

  const errLow = lastMsg.toLowerCase();
  const looksMigrated = MIGRATED_ERR_RE.test(errLow) || /\b6005\b/.test(lastMsg);

  if (looksMigrated) {
    ok = await tryPool("pump-amm");
    if (ok) return ok;
    ok = await tryPool("raydium");
    if (ok) return ok;
  }

  if (NEED_CURVE_ERR_RE.test(errLow)) {
    ok = await tryPool("pump");
    if (ok) return ok;
  }

  return { ok: false, message: lastMsg, attempts };
}

/**
 * Poll Solana RPC until the tx is confirmed or times out.
 */
export async function confirmLightningTx(
  signature: string,
  timeoutMs = 20_000,
): Promise<{ confirmed: boolean; err: string | null }> {
  const deadline = Date.now() + timeoutMs;
  const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

  while (Date.now() < deadline) {
    try {
      const res = await fetchSolanaRpc({
        jsonrpc: "2.0",
        id: 1,
        method: "getSignatureStatuses",
        params: [[signature], { searchTransactionHistory: true }],
      });
      const json = (await res.json()) as {
        result?: { value?: Array<{ confirmationStatus?: string; err: unknown } | null> };
      };
      const status = json.result?.value?.[0];
      if (status !== undefined && status !== null) {
        if (status.err !== null && status.err !== undefined) {
          return { confirmed: false, err: `Tx failed on-chain: ${JSON.stringify(status.err)}` };
        }
        const lvl = status.confirmationStatus;
        if (lvl === "confirmed" || lvl === "finalized") {
          return { confirmed: true, err: null };
        }
      }
    } catch {
      // RPC blip — retry
    }
    await delay(1500);
  }
  return { confirmed: false, err: "Tx not confirmed within timeout — check explorer" };
}
